/**
 * Browser-side writes to the canonical session, one at a time, in send order.
 *
 * E2E 17.10 (sess_ea8bf19d): a local intake reply stored its exchange (9 events, 4 s)
 * while the intake record and the next typed line (to Live) raced it. The intake record
 * lost its version check twice and was dropped, and the next line was never stored.
 */
export interface CanonicalStoreQueue {
  /** Run `task` after every earlier task. A failed task does not block later ones. */
  enqueue(task: () => Promise<void>): Promise<void>
  /** Resolves when every earlier task is done, or after `maxWaitMs`. Never rejects. */
  settled(maxWaitMs?: number): Promise<void>
}

export const CANONICAL_STORE_MAX_WAIT_MS = 8_000

export function createCanonicalStoreQueue(): CanonicalStoreQueue {
  let tail: Promise<void> = Promise.resolve()
  return {
    enqueue(task) {
      const run = tail.then(task, task)
      tail = run.catch(() => undefined)
      return run
    },
    settled(maxWaitMs = CANONICAL_STORE_MAX_WAIT_MS) {
      let timer: ReturnType<typeof setTimeout> | undefined
      const cap = new Promise<void>(resolve => {
        timer = setTimeout(resolve, maxWaitMs)
      })
      return Promise.race([tail, cap]).finally(() => clearTimeout(timer))
    },
  }
}
